import { useMemo } from "react";

import {
  Rule,
  RuleSetDecision
} from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/types";
import { BadgeVariant } from "shared/components";
import { DecisionAction } from "shared/types";
import { Application, Reasons } from "types/application";

const RECOMMENDATION_COPY = {
  [DecisionAction.Decline]: {
    title: "Declined",
    subtitle: "Why declined",
    details: "",
    badgeVariant: BadgeVariant.ERROR
  },
  [DecisionAction.Approve]: {
    title: "Approved",
    subtitle: "Why approved",
    details: "All conditions met. No reasons to decline.",
    badgeVariant: BadgeVariant.SUCCESS
  },
  [DecisionAction.ConditionalApprove]: {
    title: "Conditional approval",
    subtitle: "Why conditional approval",
    details:
      "Not enough conditions to approve or decline. Please review manually.",
    badgeVariant: BadgeVariant.WARNING
  }
};

const getActiveRules = (ruleSets: Reasons[]) =>
  ruleSets.flatMap((ruleSet) => ruleSet.rules.filter((rule) => !rule.skipped));

const isRuleSetMet = (ruleSet?: Reasons): ruleSet is Reasons => {
  if (!ruleSet) return false;
  const activeRules = ruleSet.rules.filter((rule) => !rule.skipped);
  return activeRules.length > 0 && activeRules.every((rule) => rule.result);
};

const uniqRules = (rules: Rule[]) => {
  const seen = new Set<string>();
  return rules.filter((rule) => {
    const key = `${rule.field_name}-${rule.operator}-${rule.threshold}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const resolveRecommendation = (
  explicit: DecisionAction | undefined,
  isMetDeclineConditions: boolean,
  isMetApprovalConditions: boolean
): DecisionAction => {
  if (explicit) return explicit;
  if (isMetDeclineConditions) return DecisionAction.Decline;
  if (isMetApprovalConditions) return DecisionAction.Approve;
  return DecisionAction.ConditionalApprove;
};

export const useRecommendation = (application: Application) => {
  const ruleReasons = application.rule_reasons || [];

  const declineRuleSets = ruleReasons.filter(
    (ruleSet) => ruleSet.rule_set_type === RuleSetDecision.Decline
  );
  const approvalRuleSets = ruleReasons.filter(
    (ruleSet) => ruleSet.rule_set_type === RuleSetDecision.Approve
  );

  const isMetDeclineConditions = declineRuleSets.some(isRuleSetMet);
  const isMetApprovalConditions = approvalRuleSets.some(isRuleSetMet);

  const metDeclineRuleSet = declineRuleSets.find(isRuleSetMet);
  const metApprovalRuleSet = approvalRuleSets.find(isRuleSetMet);

  const currentRecommendation = resolveRecommendation(
    application.recommendation,
    isMetDeclineConditions,
    isMetApprovalConditions
  );

  const activeApprovalRules = getActiveRules(approvalRuleSets);
  const metDeclineRules = (metDeclineRuleSet?.rules || []).filter(
    (rule) => !rule.skipped && rule.result
  );
  const metApprovalRules = (metApprovalRuleSet?.rules || []).filter(
    (rule) => !rule.skipped && rule.result
  );
  const unmetApprovalRules = activeApprovalRules.filter((rule) => !rule.result);

  const surfacedRules = useMemo(() => {
    if (currentRecommendation === DecisionAction.Decline)
      return uniqRules(metDeclineRules);

    if (currentRecommendation === DecisionAction.ConditionalApprove)
      return uniqRules([...unmetApprovalRules, ...metDeclineRules]);

    return [];
  }, [currentRecommendation, metDeclineRules, unmetApprovalRules]);

  const summaryRules = useMemo(() => {
    if (currentRecommendation === DecisionAction.Decline)
      return metDeclineRules;

    if (currentRecommendation === DecisionAction.Approve)
      return metApprovalRules;

    return [];
  }, [currentRecommendation, metApprovalRules, metDeclineRules]);

  const recommendationMeta =
    RECOMMENDATION_COPY[currentRecommendation] ||
    RECOMMENDATION_COPY[DecisionAction.ConditionalApprove];

  return {
    currentRecommendation,
    recommendationMeta,
    summaryRules,
    surfacedRules,
    isMetDeclineConditions,
    isMetApprovalConditions
  };
};
